import React from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { Field, reduxForm, getFormValues, change } from 'redux-form'
import { withRouter } from 'react-router'
import queryString from 'qs'
import debounce from 'lodash/debounce'
import get from 'lodash/get'

import normalizeKeys from 'components/utils/propsUtils'

import ServerSideContext from '../../../contexts/ServerSideContext'
import { fetchEnquiryPrices } from '../../../actions/enquiryActions'

import Button from '../../../components/display/Button'
import Overlay from '../../../components/display/Overlay'
import InstantBookBadge from '../../../components/display/InstantBookBadge'
import PricingBreakdown from '../../../components/pricing/PricingBreakdown'
import { AuthToken } from '../../../components/auth/shared'
import { formatPrice } from '../../../components/utils/pricing'
import { slugToNice } from '../../../components/utils/stringUtils'

import BookingFormFields from './BookingFormFields'
import { unbookableWithReason } from './helpers'

export const BookingFormWrapper = styled.div`
  position: relative;
  padding: 32px 24px;
  background-color: ${({ theme }) => theme.brand.sand};
  border-radius: 6px;

  .DateRangePicker {
    width: 100%;
    margin-bottom: 8px;
  }

  .SingleDatePicker {
    margin-bottom: 16px;
  }

  .DateRangePickerInput,
  .SingleDatePickerInput {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 0;
    border-color: #d5d5d5;
  }

  ${Button} {
    width: 100%;
    margin-top: 24px;
  }
`
const BookingErrors = styled.div`
  margin-bottom: 10px;
  text-align: center;
  color: ${({ theme }) => theme.colors.errorRed};
`

const Header = styled.header`
  margin-bottom: 24px;
  color: ${({ theme }) => theme.brand.forest};

  font-size: 50px;

  strong {
    font-weight: 500;
    margin-right: 8px;
  }

  small {
    font-size: 0.4em;
  }
`

const Footnote = styled.footer`
  width: 100%;
  margin-top: 16px;
  color: ${({ theme, warning }) =>
    warning
      ? theme?.['-ds-warning-300']?.value
      : theme?.['-ds-neutral-600']?.value};
  font-size: 14px;
  font-weight: 300;
  text-align: center;
`

export const validDate = (value) =>
  !value ? 'Please add dates to request a booking' : undefined

class Booking extends React.Component {
  constructor() {
    super()
    this.state = {
      submitting: false,
    }
  }

  componentDidMount() {
    const { dispatch, form, initialValues, listingId, storyId } = this.props
    const { adults, children, start_date, end_date } = initialValues.booking

    if (storyId) {
      dispatch(change(form, 'booking[story_id]', storyId))
    }

    if (start_date) {
      return dispatch(
        fetchEnquiryPrices(listingId, {
          adults,
          children,
          start_date,
          end_date,
        })
      )
    }

    return dispatch(fetchEnquiryPrices(listingId, undefined))
  }

  hasErrors = () => {
    const { enquiry } = this.props
    return enquiry.data && enquiry.data.errors.length
  }

  renderBookingErrors = () => {
    const { enquiry } = this.props

    if (this.hasErrors()) {
      return <BookingErrors>{enquiry.data.errors[0]}</BookingErrors>
    }

    return null
  }

  render() {
    const {
      listingId,
      listingBookable,
      listing,
      enquiry,
      formValues,
      handleSubmit,
      doNotShowPayoutTotal,
    } = this.props
    const { submitting } = this.state

    const hasTotal = enquiry.data && enquiry.data.total > 0
    const enquiryListing = enquiry.data && enquiry.data.listing

    const disabledReason = unbookableWithReason(listingBookable)
    const footNote = listing.instant_book_enabled
      ? 'No payment required yet'
      : 'No payment required until your host accepts'

    let canRequestBooking = false
    if (formValues && enquiryListing) {
      canRequestBooking = listing.status === 'published'
    }
    if (disabledReason) {
      canRequestBooking = false
    }

    return (
      <ServerSideContext.Consumer>
        {(serverSide) => (
          <BookingFormWrapper className="qa-booking-form">
            <form
              ref={(ref) => {
                this.form = ref
              }}
              action={`${listing.url}/bookings`}
              method="post"
              onSubmit={handleSubmit(() => {
                this.setState({ submitting: true })
                this.form.submit()
              })}
            >
              <AuthToken />

              <Header>
                <strong>
                  {hasTotal
                    ? formatPrice(enquiry.data.total, 0)
                    : formatPrice(listing.price, 0)}
                </strong>
                <small>
                  {(() => {
                    if (hasTotal) {
                      return 'total'
                    }
                    return slugToNice(listing.pricing_suffix)
                  })()}
                </small>
              </Header>

              {listing.instant_book_enabled && (
                <div style={{ position: 'relative', top: -18 }}>
                  <InstantBookBadge />
                </div>
              )}

              {this.renderBookingErrors()}

              <BookingFormFields
                formName="Booking"
                listingId={listingId}
                listing={listing}
                enquiry={enquiry}
                formValues={formValues}
              />

              <Field
                id="booking_story_id"
                name="booking[story_id]"
                component="input"
                type="hidden"
              />

              {hasTotal && enquiry.data && (
                <PricingBreakdown
                  pricing={normalizeKeys(enquiry.data)}
                  doNotShowPayoutTotal={doNotShowPayoutTotal}
                />
              )}

              <Button
                type="submit"
                disabled={!canRequestBooking}
                uppercase={false}
              >
                {listing.instant_book_enabled ? 'Book now' : 'Request to book'}
              </Button>

              <Footnote warning={disabledReason != null}>
                {disabledReason || footNote}
              </Footnote>
            </form>

            {(serverSide || submitting) && <Overlay />}
          </BookingFormWrapper>
        )}
      </ServerSideContext.Consumer>
    )
  }
}

const BookingForm = reduxForm({
  form: 'Booking',
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
})(Booking)

export default withRouter(
  connect(
    (
      { auth, listing, enquiry, myBucketlist, viewstate, ...state },
      { location, ...props }
    ) => {
      const {
        adults,
        children,
        startDate,
        endDate,
        ...queryParams
      } = queryString.parse(location.search, { ignoreQueryPrefix: true })
      return {
        auth,
        listing: listing.data,
        myBucketlist,
        enquiry,
        viewstate,
        formValues: getFormValues('Booking')(state),
        initialValues: {
          booking: {
            adults:
              parseInt(adults, 10) ||
              enquiry?.data?.adults ||
              (listing?.data?.capacity === 1 ? 1 : 2),
            children: parseInt(children, 10) || enquiry?.data?.children || 0,
            start_date: startDate || get(enquiry.data, 'start_date', null),
            end_date:
              endDate || startDate || get(enquiry.data, 'end_date', null),
            /**
             * We need to either use the storyId passed as props (for stories)
             * or the one passed as queryParams in the URL (for listings)
             */
            story_id: props.storyId || queryParams.story_id,
          },
        },
      }
    },
    (dispatch, props) => {
      const debouncedFetchPrices = debounce((booking) => {
        dispatch(fetchEnquiryPrices(props.listingId, booking))
      }, 250)

      const onChange = ({ booking }) => {
        if (booking.start_date && booking.end_date) {
          debouncedFetchPrices(booking)
        }
      }

      return {
        onChange,
      }
    }
  )(BookingForm)
)
